import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

/**
 * IndividualPANInput
 *
 * Supports two usage patterns:
 *
 * 1. Controlled (JourneyPage / builder pattern):
 *    <IndividualPANInput value={{ individual_pan_number: "ABCDE1234F" }} onChange={fn} />
 *
 * 2. Legacy (direct store pattern via initialData):
 *    <IndividualPANInput initialData={{ individual_pan_number: "ABCDE1234F" }} onDataChange={fn} />
 *
 * The `value` / `onChange` pattern takes precedence when provided.
 */
interface IndividualPANInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { individual_pan_number?: string | null };
  onChange?: (val: { individual_pan_number: string }) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  /** Gate-level field errors passed down from JourneyPage after Continue is pressed */
  fieldErrors?: Record<string, string>;

  // Legacy pattern (kept for backward compatibility)
  initialData?: { individual_pan_number?: string | null };
  onDataChange?: (data: { individual_pan_number: string }) => void;
}

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function validatePAN(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return 'PAN number is required';
  if (trimmed.length !== 10) return 'PAN must be exactly 10 characters';
  if (!PAN_REGEX.test(trimmed)) return 'Invalid PAN format (e.g. ABCDE1234F)';
  return '';
}

const IndividualPANInput: React.FC<IndividualPANInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  fieldErrors,
  initialData,
  onDataChange,
}) => {
  const panNumber =
    (value?.individual_pan_number ?? initialData?.individual_pan_number ?? '') || '';

  const [touched, setTouched] = useState(false);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      setTouched(true);
      const pan = (valueRef.current?.individual_pan_number ?? '') || '';
      return validatePAN(pan) === '';
    });
  }, [registerSubmit]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    if (onChange) onChange({ individual_pan_number: cleanValue });
    onDataChange?.({ individual_pan_number: cleanValue });
  }, [onChange, onDataChange]);

  const error = fieldErrors?.['individual_pan_number'] || (touched ? validatePAN(panNumber) : '');

  return (
    <div className="space-y-1.5">
      <Label htmlFor="individual_pan_number" className="text-gray-500 font-medium">
        PAN Number <span className="text-red-500">*</span>
      </Label>
      <Input
        id="individual_pan_number"
        type="text"
        value={panNumber}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="ABCDE1234F"
        disabled={disabled}
        maxLength={10}
        className={`border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 uppercase ${error ? 'border-red-400 focus:border-red-500' : ''}`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-0.5">{error}</p>
      )}
    </div>
  );
};

export default IndividualPANInput;
